import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
	createBrowserSupabaseClient,
	createServerSupabaseClient,
} from '@supabase/auth-helpers-nextjs';
import { encode } from 'querystring';

import { ListingCarousel } from '@components/programme/listingCarousel/ListingCarousel';
import { Programme } from '@components/programme/Programme';
import { ProgrammeFilterForm } from '@components/programme/ProgrammeFilterForm';
import { Seo } from '@components/seo/Seo';

import { serverSideWithSiteData } from '@common/utils/page';
import {
	getFilterState,
	getQueryArguments,
} from '@common/utils/programmeFilter';
import { queryFromDato } from '@data/datocms/client';
import { programmePage } from '@data/datocms/queries/programme';
import { getEventList } from '@data/supabase/getEventList';
import { getEventData } from '@data/supabase/shared';
import {
	ElsewherePresentsIndexPageResponse,
	EventData,
	FilterQueryArguments,
	ProgrammeIndexResponse,
	SiteData,
	SupabaseEventRecord,
} from '@interfaces';

interface EventIndexPageProps extends ProgrammeIndexResponse {
	highlightEvents: SupabaseEventRecord[];
	initialViewState: 'grid' | 'list';
	initialFilterState: [string, string][];
	initialEventData: EventData;
	initialSearchParamsString: string;
}

export default function EventIndexPage({
	site: { faviconMetaTags },
	programme,
	highlightEvents,
	initialViewState,
	initialFilterState,
	initialEventData,
	initialSearchParamsString,
}: EventIndexPageProps & SiteData) {
	const router = useRouter();
	const [viewState, setViewState] = useState(initialViewState);
	const [filterState, setFilterState] = useState(new Map(initialFilterState));
	const [eventData, setEventData] = useState(initialEventData);
	const [isDataLoading, setIsDataLoading] = useState(false);
	const supabaseClient = createBrowserSupabaseClient();
	const [searchParamsString, setSearchParamsString] = useState(
		initialSearchParamsString,
	);

	const loadData = async (
		queryArguments: FilterQueryArguments,
		pageNumber = 1,
	) => {
		setIsDataLoading(true);

		const nextEventData = await getEventData({
			supabaseClient,
			queryArguments,
			pageNumber,
		});

		setEventData(nextEventData);

		setIsDataLoading(false);
	};

	useEffect(() => {
		// Reacts to changes to the query string
		const searchParams = new URLSearchParams(encode(router.query));

		searchParams.sort();

		const nextSearchParamsString = searchParams.toString();

		if (searchParamsString !== nextSearchParamsString) {
			// Get query arguments to fetch the events
			const queryArguments = getQueryArguments({
				data: searchParams,
				programme,
			});

			loadData(queryArguments, Number(searchParams.get('page') || 1));

			// Update filter state
			const nextFilterState = getFilterState({
				data: searchParams,
				programme,
			});

			setFilterState(new Map(nextFilterState));

			setSearchParamsString(nextSearchParamsString);
		}
	}, [router.query]);

	return (
		<>
			<Seo metaTagData={[...programme.seoMetaTags, ...faviconMetaTags]} />
			<h1 className="sr-only">{programme.title}</h1>
			{highlightEvents && programme.highlightSection ? (
				<ListingCarousel
					events={highlightEvents}
					title={programme.highlightSection.title}
				/>
			) : null}
			<ProgrammeFilterForm
				filterState={filterState}
				programme={programme}
				setFilterState={setFilterState}
				setViewState={setViewState}
				viewState={viewState}
			/>
			<Programme
				events={eventData.events}
				isLoading={isDataLoading}
				paginationData={{
					pageNumber: eventData.pageNumber,
					hasNextPage: eventData.hasNextPage,
				}}
				view={viewState}
			/>
		</>
	);
}

export const getServerSideProps = serverSideWithSiteData<EventIndexPageProps>(
	async (context) => {
		const { query } = context;
		const supabaseClient = createServerSupabaseClient(context);

		const pageNumber = Number(query.page) || 1;

		const datoResponse = await queryFromDato<
			ProgrammeIndexResponse & ElsewherePresentsIndexPageResponse
		>({
			query: programmePage,
		});

		const programme = datoResponse?.programme;

		if (!programme) {
			return {
				notFound: true,
			};
		}

		// Convert query to searchParams
		const searchParams = new URLSearchParams(encode(query));

		searchParams.sort();

		// Get the query arguments to query events from the database
		const queryArguments = getQueryArguments({
			data: searchParams,
			programme,
		});

		const initialEventData = await getEventData({
			supabaseClient,
			queryArguments,
			pageNumber,
		});

		const initialViewState =
			searchParams.get('view') === 'grid' ? 'grid' : 'list';
		const initialFilterState = getFilterState({
			data: searchParams,
			programme,
		});

		const { events: highlightEvents } = await getEventList({
			options: {
				elsewherePresents: false,
				excludeDice: true,
				highlight: true,
				range: {
					from: 0,
					to: 15,
				},
			},
			supabaseClient,
		});

		return {
			props: {
				programme,
				highlightEvents,
				initialViewState,
				initialFilterState,
				initialEventData,
				initialSearchParamsString: searchParams.toString(),
			},
		};
	},
);
